import { useMemo } from "react";
import { cx } from "@emotion/css";
import { IOption } from "./AutocompleteSelect";
import { MultiValue, SingleValue } from "react-select";
import { GroupTagRecord, isTagPrivate, LabelTagRecord, RecordValue } from "libs/schema";
import Select from "react-select/async";
import { useMentionableUsers } from "~/hooks/useMentionableUsers";
import { ClientEnvironment } from "~/environment/ClientEnvironment";
import { getTagFolderAncestors } from "~/queries/getTagFolderAncestors";
import { renderGroupName } from "~/utils/tag-utils";
import { useAuthGuardContext } from "~/route-guards/withAuthGuard";
import { useUsersGroupsWithFolderAncestorRecords } from "~/hooks/useUsersGroupsWithFolderAncestorRecords";
import { usePersonalLabels } from "~/hooks/usePersonalLabels";
import { useAsync } from "react-use";
import commandScore from "command-score";

/* -------------------------------------------------------------------------------------------------
 * UserSelect
 * -----------------------------------------------------------------------------------------------*/

export interface IUserOption extends IOption<string> {}
export interface UserSelectProps<M extends boolean> extends Omit<BaseSelectProps<IUserOption, M>, "name" | "options"> {}

/* -----------------------------------------------------------------------------------------------*/

export async function getUserSelectOption(
  environment: Pick<ClientEnvironment, "recordLoader">,
  userId: string,
): Promise<IUserOption | null> {
  const { recordLoader } = environment;

  const [userProfile] = await recordLoader.getRecord({ table: "user_profile", id: userId });

  if (!userProfile) return null;

  return {
    label: userProfile.name,
    value: userProfile.id,
  };
}

/* -----------------------------------------------------------------------------------------------*/

/**
 * Component for selecting a group with autocomplete.
 */
export function UserSelect<M extends boolean>(props: UserSelectProps<M>) {
  const [users] = useMentionableUsers();

  const options: IUserOption[] = useMemo(() => {
    return users.map((user) => {
      return {
        label: user.profile.name,
        value: user.id,
      };
    });
  }, [users]);

  return <BaseSelect {...props} name="users" options={options} />;
}

/* -------------------------------------------------------------------------------------------------
 * GroupSelect
 * -----------------------------------------------------------------------------------------------*/

export interface IGroupOption extends IOption<string> {
  folderPaths: RecordValue<"tag">[][];
  isPrivate: boolean;
}

export interface GroupSelectProps<M extends boolean>
  extends Omit<BaseSelectProps<IGroupOption, M>, "options" | "name"> {
  groupFilterFn?: (group: GroupTagRecord) => boolean;
  isOptionDisabledFn?: (props: { group: GroupTagRecord }) => {
    isDisabled: boolean;
    disabledReason: string | undefined;
  };
}

/* -----------------------------------------------------------------------------------------------*/

export async function getGroupSelectOption(
  environment: Pick<ClientEnvironment, "recordLoader">,
  groupId: string,
): Promise<IGroupOption | null> {
  const { recordLoader } = environment;

  const [[group], { folderPaths }] = await Promise.all([
    recordLoader.getRecord({ table: "tag", id: groupId }),
    getTagFolderAncestors(environment, { tagId: groupId }),
  ]);

  if (!group) return null;

  return {
    label: renderGroupName(group),
    value: group.id,
    folderPaths,
    isPrivate: isTagPrivate(group),
  };
}

/* -----------------------------------------------------------------------------------------------*/

/**
 * Component for selecting a group with autocomplete.
 */
export function GroupSelect<M extends boolean>(props: GroupSelectProps<M>) {
  const { groupFilterFn } = props;
  const { currentUser } = useAuthGuardContext();

  const [groups] = useUsersGroupsWithFolderAncestorRecords({
    userId: currentUser.id,
  });

  const options: IGroupOption[] = useMemo(() => {
    const isOptionDisabledFn = props.isOptionDisabledFn || (() => ({ isDisabled: false, disabledReason: undefined }));

    const filteredGroups =
      groupFilterFn ? groups.filter(({ group }) => groupFilterFn(group as GroupTagRecord)) : groups;

    return filteredGroups.map(({ group, folderPaths }) => {
      const { isDisabled, disabledReason } = isOptionDisabledFn({
        group: group as GroupTagRecord,
      });

      return {
        label: renderGroupName(group),
        value: group.id,
        folderPaths,
        isPrivate: isTagPrivate(group),
        isDisabled,
        disabledReason,
      };
    });
  }, [groups, props.isOptionDisabledFn, groupFilterFn]);

  return <BaseSelect {...props} name="groups" options={options} />;
}

/* -------------------------------------------------------------------------------------------------
 * LabelSelect
 * -----------------------------------------------------------------------------------------------*/

export interface ILabelOption extends IOption<string> {}

export interface LabelSelectProps<M extends boolean>
  extends Omit<BaseSelectProps<ILabelOption, M>, "options" | "name"> {
  labelFilterFn?: (label: LabelTagRecord) => boolean;
  isOptionDisabledFn?: (props: { label: LabelTagRecord }) => {
    isDisabled: boolean;
    disabledReason: string | undefined;
  };
}

/* -----------------------------------------------------------------------------------------------*/

export async function getLabelSelectOption(
  environment: Pick<ClientEnvironment, "recordLoader">,
  labelId: string,
): Promise<ILabelOption | null> {
  const { recordLoader } = environment;

  const [label] = await recordLoader.getRecord("tag", labelId);

  if (!label) return null;

  return {
    label: label.name,
    value: label.id,
  };
}

/* -----------------------------------------------------------------------------------------------*/

/**
 * Component for selecting a label with autocomplete.
 */
export function LabelSelect<M extends boolean>(props: LabelSelectProps<M>) {
  const { labelFilterFn } = props;

  const [labels] = usePersonalLabels();

  const options: ILabelOption[] = useMemo(() => {
    const isOptionDisabledFn = props.isOptionDisabledFn || (() => ({ isDisabled: false, disabledReason: undefined }));

    const filteredLabels =
      labelFilterFn ?
        labels.filter((label): label is LabelTagRecord => labelFilterFn(label as LabelTagRecord))
      : labels;

    return filteredLabels.map((label) => {
      const { isDisabled, disabledReason } = isOptionDisabledFn({ label });

      return {
        label: label.name,
        value: label.id,
        isDisabled,
        disabledReason,
      };
    });
  }, [labels, props.isOptionDisabledFn, labelFilterFn]);

  return <BaseSelect {...props} name="labels" options={options} />;
}

/* -------------------------------------------------------------------------------------------------
 * BaseSelect
 * -----------------------------------------------------------------------------------------------*/

export interface BaseSelectProps<Option extends IOption, M extends boolean> {
  name: string;
  value?: M extends true ? MultiValue<Option> : SingleValue<Option>;
  touched?: boolean;
  error?: string;
  options?: Option[];
  loadOptions?: (input: string) => Promise<Option[]>;
  onChange?: M extends true ? (newValue: MultiValue<Option>) => void : (newValue: SingleValue<Option>) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  autoFocus?: boolean;
  multiple?: M;
  label?: string;
  placeholder?: string;
  errorPlaceholder?: string;
}

/* -----------------------------------------------------------------------------------------------*/

export function BaseSelect<Option extends IOption, M extends boolean>(props: BaseSelectProps<Option, M>) {
  // We need to use the async loadOptions function if we want the ability up change both the options and
  // the order of the options in response to user input.
  const loadOptions = useMemo((): ((input: string) => Promise<Option[]>) => {
    if (props.loadOptions) return props.loadOptions;

    const { options } = props;

    if (!options) return async () => [];

    return async (input: string) =>
      options
        .map((o) => ({ option: o, score: commandScore(o.label, input) }))
        .sort((a, b) => b.score - a.score)
        .slice(0, 5)
        .filter((o) => o.score > 0)
        .map((o) => o.option);
  }, [props.loadOptions, props.options]);

  const defaultOptions = useAsync(() => loadOptions(""), [loadOptions]);

  const customStyles = useMemo(
    () => ({
      control: (provided: any) => ({
        ...provided,
        border: "none", // Remove border
        boxShadow: "none", // Remove box shadow
        zIndex: 1,
      }),
      menu: (provided: any) => ({
        ...provided,
        zIndex: 150,
        pointerEvents: "auto",
      }),
      menuPortal: (provided: any) => ({
        ...provided,
        zIndex: 150,
        pointerEvents: "auto",
      }),
      singleValue: (provided: any) => ({
        ...provided,
        fontSize: "0.95rem",
        backgroundColor: "transparent",
      }),
      multiValue: (provided: any) => ({
        ...provided,
        backgroundColor: "transparent",
        border: "1px solid grey",
        borderRadius: ".25rem",
      }),
      multiValueRemove: (provided: any) => ({
        ...provided,
        backgroundColor: "transparent",
        border: "none",
      }),
      placeholder: (provided: any) => ({
        ...provided,
        color: "lightgray",
      }),
    }),
    [],
  );

  return (
    <div className={cx("flex flex-1 items-center py-2")}>
      {props.label && (
        <label className={cx("my-1 mr-2", props.touched && props.error ? "text-red-9" : "text-slateDark-11")}>
          {props.label}
        </label>
      )}

      <Select
        name={props.name}
        value={props.value}
        onBlur={props.onBlur}
        isClearable={!props.multiple}
        onChange={props.onChange as (newValue: MultiValue<Option> | SingleValue<Option>) => void}
        defaultOptions={defaultOptions.value}
        loadOptions={loadOptions}
        placeholder={props.touched && props.error ? props.errorPlaceholder : props.placeholder}
        autoFocus={props.autoFocus}
        isMulti={props.multiple}
        className="w-full"
        styles={customStyles}
        menuPosition="absolute"
        menuPortalTarget={document.body}
      />
    </div>
  );
}

/* -----------------------------------------------------------------------------------------------*/
